import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Input } from '@/components/form/fields/Input/Input';
import { Select } from '@/components/form/fields/Select/Select';
import { Notification } from '@/components/Notification/Notification';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { PlatformConnectionType } from '@/types/invoiceVaultTypes';
import { getApiKeys } from '@/bundle/_MasterAdmin/_DevelopersPage/api/api';
import { connectPlatform, disconnectPlatform } from '../../api/api';
import { COLORS } from '@/styles/colors';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { StatusPill } from '../shared/StatusCell/styles';

type PropsType = { platform: PlatformConnectionType | null; canEdit: boolean; onChanged: () => void };

export const PlatformConnectionCard = ({ platform, canEdit, onChanged }: PropsType) => {
  const [name, setName] = useState('');
  const [apiKeyId, setApiKeyId] = useState({ value: '', label: '' });
  const [error, setError] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const { data: keysResponse } = useQuery({
    queryKey: ['developers_api_keys', 'invoice_vault'],
    queryFn: () => getApiKeys({ page: 1, limit: 50 }),
    enabled: canEdit,
  });
  const keyOptions = (keysResponse?.body?.results ?? [])
    .filter((k) => k.is_active)
    .map((k) => ({ value: k.id, label: k.name }));

  const connect = useMutation({
    mutationFn: () => {
      const payload = {
        platform_name: name.trim(),
        api_key_id: apiKeyId.value || undefined,
      };

      return connectPlatform(payload);
    },
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      amplitudeService.logEvent(AMPLITUDE_EVENTS.PlatformConnected);
      setError('');
      onChanged();
    },
  });
  const disconnect = useMutation({
    mutationFn: disconnectPlatform,
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);
      setConfirmDisconnect(false);
      onChanged();
    },
  });

  const connected = platform?.status === 'connected';

  return (
    <Box display='flex' flexDirection='column' gap='12px'>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='subTitle'>Invoicing platform</Typography>
        <StatusPill
          $bg={connected ? COLORS.green[200] : COLORS.grey[200]}
          $fg={connected ? COLORS.green[600] : COLORS.grey[600]}
        >
          {connected ? 'Connected' : 'Not connected'}
        </StatusPill>
      </Box>

      {connected ? (
        <>
          <Typography variant='bodySmall'>
            <b>{platform.platform_name}</b> creates a verified payment link on every invoice it issues, through the API
            key <b>{platform.api_key?.name ?? 'not set'}</b>. Connected{' '}
            {formatDate(platform.connected_at, DATE_TIME_FORMAT)}.
          </Typography>
          {canEdit && (
            <ButtonNew size='medium' variant='danger' onClick={() => setConfirmDisconnect(true)}>
              Disconnect
            </ButtonNew>
          )}
        </>
      ) : (
        <>
          <Typography variant='bodySmall'>
            Connect the platform that issues your invoices. It calls <code>POST /papi/v1/invoices/secure-link</code>{' '}
            with an API key from the Developers page and gets back the link, QR and snippet for each invoice.
          </Typography>
          {canEdit ? (
            <Box display='grid' gridTemplateColumns='1fr 1fr auto' gap='12px' alignItems='end'>
              <Input
                name='platform_name'
                label='Platform name'
                placeholder='[Invoicing platform]'
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Select
                label='API key'
                options={keyOptions}
                value={apiKeyId}
                onChange={setApiKeyId}
                placeholder='Choose a key'
              />
              <ButtonNew
                size='medium'
                disabled={!name.trim()}
                isLoading={connect.isPending}
                onClick={() => connect.mutate()}
              >
                Connect
              </ButtonNew>
            </Box>
          ) : (
            <Notification variant='info'>Ask a master admin to connect the platform.</Notification>
          )}
          {error && <Notification variant='error'>{error}</Notification>}
          <Typography variant='caption'>
            OAuth sign-in with the platform is a follow-up (Part E 2). Today the connection is the API key the platform
            uses.
          </Typography>
        </>
      )}

      {confirmDisconnect && (
        <ConfirmModal
          isOpen
          header='Disconnect the platform?'
          body='New invoices from the platform will no longer get a WireVault link. Existing links keep working until they expire.'
          confirmText='Disconnect'
          confirmButtonVariant='danger'
          isLoading={disconnect.isPending}
          onConfirm={() => disconnect.mutate()}
          onClose={() => setConfirmDisconnect(false)}
        />
      )}
    </Box>
  );
};
